import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import Dropdown from "@/components/ui/Dropdown";
import MarketingHistoryTable from "@/components/marketing/MarketingHistoryTable";
import MarketingHistoryRecipientsModal from "@/components/marketing/MarketingHistoryRecipientsModal";
import MarketingHistoryDetailsModal from "@/components/marketing/MarketingHistoryDetailsModal";
import Pagination from "@/components/Common/Pagination";
import { marketingHistoryService } from "@/services/marketingHistoryService";
import usePaginationStore from "@/hooks/stores/usePaginationStore";
import { useDebounce } from "@/utils/helpers";

const HISTORY_TABLE_KEY = "marketing-history-sends-table";
const DEFAULT_PER_PAGE = 50;

const CHANNEL_LABELS = {
  email: "Email",
  sms: "SMS",
};

const CHANNEL_FILTER_OPTIONS = [
  { value: "email", label: "Email" },
  { value: "sms", label: "SMS" },
];

const normalizeHistoryRow = (item, index) => ({
  id: item.id ?? item.batch_id ?? `send-${index}`,
  name:
    item.campaign_name || item.campaign?.name || item.name || "-",
  recipients_count:
    item.recipients_count ??
    item.total_candidates ??
    item.candidates_count ??
    item.candidate_count ??
    0,
  total_sent:
    item.total_sent ??
    item.sent_count ??
    item.emails_sent ??
    item.sms_sent ??
    item.success_count ??
    0,
  channel: item.channel || "-",
  sent_at: item.sent_at || null,
  email_body: item.email_body || "",
  sms_body: item.sms_body || "",
});

const extractList = (response) => {
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.batches)) return response.batches;
  if (Array.isArray(response?.sends)) return response.sends;
  if (Array.isArray(response)) return response;
  return [];
};

const extractPagination = (response, fallback) => {
  const pagination = response?.pagination || response?.meta || {};

  return {
    current_page:
      pagination.current_page ?? response?.current_page ?? fallback.current_page,
    per_page: pagination.per_page ?? response?.per_page ?? fallback.per_page,
    total: pagination.total ?? response?.total ?? fallback.total,
    last_page: pagination.last_page ?? response?.last_page ?? fallback.last_page,
  };
};

const MarketingHistory = () => {
  const { getPerPage, setPerPage } = usePaginationStore();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [tempSearchTerm, setTempSearchTerm] = useState("");
  const [channelFilter, setChannelFilter] = useState("");
  const [recipientModal, setRecipientModal] = useState({
    isOpen: false,
    batchId: null,
    batchName: "",
  });
  const [detailsModal, setDetailsModal] = useState({
    isOpen: false,
    row: null,
  });
  const [serverPagination, setServerPagination] = useState({
    current_page: 1,
    per_page: getPerPage(HISTORY_TABLE_KEY) || DEFAULT_PER_PAGE,
    total: 0,
    last_page: 1,
  });

  const selectedChannelOption = useMemo(
    () =>
      CHANNEL_FILTER_OPTIONS.find((option) => option.value === channelFilter) || {
        label: "All Channels",
        value: "",
      },
    [channelFilter],
  );

  const updateSearchTerm = (term) => {
    setSearchQuery(term);
    setServerPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const debouncedSearch = useDebounce(updateSearchTerm, 500);

  const handleSearchChange = (term) => {
    setTempSearchTerm(term);
    debouncedSearch(term);
  };

  const handleChannelFilterChange = (selectedOption) => {
    setChannelFilter(selectedOption?.value ?? "");
    setServerPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const handlePageChange = (page) => {
    setServerPagination((prev) => ({
      ...prev,
      current_page: page,
    }));
  };

  const handlePerPageChange = (selectedOption) => {
    const newPerPage = selectedOption.value;
    setPerPage(HISTORY_TABLE_KEY, newPerPage);
    setServerPagination((prev) => ({
      ...prev,
      per_page: newPerPage,
      current_page: 1,
    }));
  };

  const handleCountClick = (row) => {
    if (!row.id || !(row.recipients_count ?? row.total_sent)) return;

    setRecipientModal({
      isOpen: true,
      batchId: row.id,
      batchName: row.name,
    });
  };

  const handleCloseRecipientModal = () => {
    setRecipientModal({
      isOpen: false,
      batchId: null,
      batchName: "",
    });
  };

  const handleViewClick = (row) => {
    setDetailsModal({
      isOpen: true,
      row,
    });
  };

  const handleCloseDetailsModal = () => {
    setDetailsModal({
      isOpen: false,
      row: null,
    });
  };

  const loadHistory = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const params = {
        page: serverPagination.current_page,
        per_page: serverPagination.per_page || DEFAULT_PER_PAGE,
      };

      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      if (channelFilter) {
        params.channel = channelFilter;
      }

      const response = await marketingHistoryService.getCampaignHistory(params);
      const list = extractList(response).map(normalizeHistoryRow);

      setRows(list);
      setServerPagination((prev) => extractPagination(response, prev));
    } catch (err) {
      setError(err.message || "Failed to load marketing history");
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, [
    searchQuery,
    channelFilter,
    serverPagination.current_page,
    serverPagination.per_page,
  ]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  return (
    <div className="flex flex-col gap-5 sm:gap-6">
      {error && (
        <div className="p-3 sm:p-4 bg-red-100 text-red-700 rounded-md border border-red-200 text-sm">
          {error}
        </div>
      )}

      <div className="relative z-10 flex w-full flex-wrap items-center gap-3">
        <div className="w-full sm:max-w-[384px] shrink-0">
          <div className="self-stretch h-11 px-3.5 py-2.5 bg-background rounded-lg border border-input inline-flex justify-start items-center gap-2 overflow-hidden w-full">
            <Search className="w-5 h-5 text-muted-foreground shrink-0" />
            <input
              id="marketing-history-search"
              type="text"
              placeholder="Search campaigns"
              value={tempSearchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="flex-1 text-foreground placeholder:text-muted-foreground text-base font-normal leading-6 outline-none bg-transparent"
            />
          </div>
        </div>

        <div className="w-full sm:w-[180px] shrink-0 ml-auto">
          <Dropdown
            className="w-full"
            options={CHANNEL_FILTER_OPTIONS}
            value={selectedChannelOption}
            placeholder="All Channels"
            onChange={handleChannelFilterChange}
            allowAll
            allLabel="All Channels"
            triggerClassName="!bg-background !border !border-input hover:!border-primary !h-11"
          />
        </div>
      </div>

      <MarketingHistoryTable
        rows={rows}
        isLoading={isLoading}
        searchTerm={searchQuery}
        channelLabels={CHANNEL_LABELS}
        onCountClick={handleCountClick}
        onViewClick={handleViewClick}
      />

      {!isLoading && (
        <Pagination
          currentPage={serverPagination.current_page}
          totalPages={serverPagination.last_page}
          per_page={serverPagination.per_page}
          onPageChange={handlePageChange}
          onPerPageChange={handlePerPageChange}
        />
      )}

      <MarketingHistoryRecipientsModal
        isOpen={recipientModal.isOpen}
        onClose={handleCloseRecipientModal}
        batchId={recipientModal.batchId}
        batchName={recipientModal.batchName}
      />

      <MarketingHistoryDetailsModal
        isOpen={detailsModal.isOpen}
        onClose={handleCloseDetailsModal}
        row={detailsModal.row}
        channelLabels={CHANNEL_LABELS}
      />
    </div>
  );
};

export default MarketingHistory;
